/**
 * Deposit pay page (owner request, 17 October 2026): `widget/pay/<token>`, opened from the link a
 * PA sent on WhatsApp or by email. Shows the amount and the appointment, then the card payment on
 * the clinic's own Stripe account. Paid only when the webhook says so: the page waits for it.
 */
import { useEffect, useMemo, useState } from "preact/hooks";
import { t, type Locale } from "@wapadesk/i18n";
import type { PayPageResponse } from "@wapadesk/schema";
import { ApiClientError } from "../lib/api";
import { PayApi } from "../lib/pay-api";
import type { PaymentState } from "../store";
import { Payment } from "./Payment";

function money(minor: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

export function PayPage({ token, apiOrigin }: { token: string; apiOrigin: string }) {
  const api = useMemo(() => new PayApi(apiOrigin), [apiOrigin]);
  const [page, setPage] = useState<PayPageResponse | undefined>();
  const [missing, setMissing] = useState(false);
  const [payment, setPayment] = useState<PaymentState | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    api
      .page(token)
      .then(setPage)
      .catch(() => setMissing(true));
  }, [api, token]);

  const locale = (page?.clinic.defaultLocale ?? "en-GB") as Locale;
  const amount = page ? money(page.amount, page.currency, locale) : "";

  const start = async () => {
    if (!page) return;
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.intent(token);
      setPayment({
        appointmentId: "",
        clientSecret: r.clientSecret,
        stripeAccountId: r.stripeAccountId,
        publishableKey: r.publishableKey,
        amount: page.amount,
        currency: page.currency,
        holdExpiresAt: "",
        phase: "pay",
      });
    } catch (e) {
      setError(
        e instanceof ApiClientError && e.code === "not_payable"
          ? t(locale, "pay.closed")
          : t(locale, "pay.unavailable"),
      );
    } finally {
      setBusy(false);
    }
  };

  // After the card step: wait for the webhook to mark the request paid (up to about 30 seconds).
  const confirmed = async () => {
    setPayment((p) => (p ? { ...p, phase: "confirming" } : p));
    for (let i = 0; i < 15; i++) {
      const p = await api.page(token).catch(() => undefined);
      if (p?.status === "paid") {
        setPage(p);
        setPayment(undefined);
        return;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    // The card went through; the webhook may simply be slow. Say so plainly.
    setPage((p) => (p ? { ...p, status: "paid" } : p));
    setPayment(undefined);
  };

  const shell = (body: preact.ComponentChildren) => (
    <main class="bp" style={page ? `--bp-accent:${page.clinic.color}` : undefined}>
      <header class="bp-head">
        <span class="bp-name">{page?.clinic.name ?? ""}</span>
      </header>
      <div class="bp-body">{body}</div>
      <footer class="bp-foot">
        <a
          href="https://wapadesk.com/?utm_source=widget&utm_medium=badge&utm_campaign=pay_page"
          target="_blank"
          rel="noopener"
        >
          Powered by Wapadesk
        </a>
      </footer>
    </main>
  );

  if (missing) return shell(<p class="bp-note">{t("en-GB", "pay.notFound")}</p>);
  if (!page)
    return shell(
      <div class="bp-loading" aria-busy="true">
        <div class="spinner" aria-label="Loading" />
      </div>,
    );

  if (page.status === "paid")
    return shell(
      <div class="bp-thanks" role="status">
        <svg class="bp-tick" viewBox="0 0 52 52" width="52" height="52" aria-hidden="true">
          <circle cx="26" cy="26" r="24" />
          <path d="M15 27 l7 7 l15 -16" />
        </svg>
        <div>
          <strong>{amount}</strong>
          <p>
            {t(locale, "pay.paid").replace("{name}", page.firstName ? ` ${page.firstName}` : "")}
          </p>
        </div>
      </div>,
    );
  if (page.status !== "requested")
    return shell(
      <p class="bp-note">
        {t(locale, page.status === "refunded" ? "pay.refunded" : "pay.closed")}
      </p>,
    );

  if (payment)
    return shell(
      <Payment
        locale={locale}
        payment={payment}
        title={t(locale, "pay.payTitle")}
        note={t(locale, "pay.payNote")}
        onConfirmed={confirmed}
        onFailed={() => undefined}
        onCancel={() => setPayment(undefined)}
      />,
    );

  const appt = page.appointment;
  const when = appt
    ? new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: appt.timezone,
      }).format(new Date(appt.startAt))
    : undefined;
  return shell(
    <div class="bp-stack pay-page">
      <h1 class="bp-title">{t(locale, "pay.title")}</h1>
      <p>
        {page.firstName ? `${page.firstName}, ` : ""}
        {t(locale, "pay.intro").replace("{clinic}", page.clinic.name).replace("{amount}", amount)}
      </p>
      <div class="bp-card pay-amount" aria-label={amount}>
        <strong>{amount}</strong>
        {page.description && <span class="bp-sub">{page.description}</span>}
      </div>
      {appt && (
        <div class="bp-card">
          <strong>{t(locale, "pay.forAppointment")}</strong>
          <span class="bp-sub">
            {appt.typeName} · {when}
            {appt.mode === "online"
              ? " · Online"
              : appt.locationName
                ? ` · ${appt.locationName}`
                : ""}
          </span>
        </div>
      )}
      {!page.payable && <p class="bp-note">{t(locale, "pay.unavailable")}</p>}
      {error && (
        <p class="bp-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        class="bp-primary"
        disabled={!page.payable || busy}
        onClick={() => void start()}
      >
        {t(locale, "pay.continue")} · {amount}
      </button>
    </div>,
  );
}
